import { LODGE_EMAIL, LODGE_EMAIL_HREF, LODGE_PHONE_DISPLAY, LODGE_PHONE_HREF, callAriaLabel } from "@/lib/contact-info";
import { createFileRoute, Link } from "@tanstack/react-router";
import { seoHead } from "@/lib/seo";

export const Route = createFileRoute("/privacy")({
  head: () =>
    seoHead({
      path: "/privacy",
      title: "Privacy Policy — Coffman Cove Lodging",
      description:
        "How Coffman Cove Lodging collects, uses, and protects information from guests booking lodging, meals, and boat rentals on Prince of Wales Island, Alaska.",
      extraMeta: [{ name: "robots", content: "index,follow" }],
    }),
  component: PrivacyPage,
});

const lastUpdated = "June 30, 2026";

function PrivacyPage() {
  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 py-16 sm:py-20 text-[var(--offwhite)]">
      <p className="text-xs uppercase tracking-[0.22em] text-[var(--electric)]">Legal</p>
      <h1 className="mt-2 font-display text-4xl md:text-5xl text-white">Privacy Policy</h1>
      <p className="mt-3 text-sm text-[var(--offwhite-warm)]/70">Last updated {lastUpdated}</p>

      <p className="mt-8 rounded-xl border border-[var(--electric)]/20 bg-[var(--electric)]/5 p-4 text-sm leading-relaxed text-[var(--offwhite-warm)]/85">
        This page is maintained by Coffman Cove Lodging to explain how we handle information
        you share when booking a stay, contacting us, or using our website. It is not a legal
        contract — please contact us with any questions.
      </p>

      <Section title="Information we collect">
        We collect the information you give us directly — your name, contact details, party
        size, requested dates, payment details for a deposit, and any preferences you share
        about your trip. Our website also collects basic technical data (pages viewed,
        approximate location, device type) to keep the site running and improve it.
      </Section>

      <Section title="How we use it">
        We use your information to confirm reservations, prepare for your arrival, send trip
        details, respond to questions, process payments, and occasionally share lodge news
        with past guests. We do not sell your personal information.
      </Section>

      <Section title="Who we share it with">
        We share information only with service providers that help us operate — for example,
        payment processors, email/SMS providers, and the hosting platform that runs this
        website. Each is bound to use your information only for the service they provide to
        us.
      </Section>

      <Section title="Cookies and analytics">
        Our site uses a small number of cookies and similar technologies to remember
        preferences (such as units and timezone on the Fishing Report) and to measure traffic
        in aggregate. You can clear cookies any time in your browser.
      </Section>

      <Section title="Data retention">
        Reservation records are kept as long as needed to honor stays and for normal
        accounting and tax requirements. You can ask us to delete personal information we
        are not legally required to keep.
      </Section>

      <Section title="Your choices">
        You can request a copy of the information we hold about you, ask us to correct it,
        or opt out of marketing emails at any time. Email{" "}
        <a className="text-[var(--electric)] hover:underline" href={LODGE_EMAIL_HREF}>
          {LODGE_EMAIL}
        </a>{" "}
        or call{" "}
        <a
          className="inline-flex min-h-11 items-center text-[var(--electric)] hover:underline"
          href={LODGE_PHONE_HREF}
          aria-label={callAriaLabel(LODGE_PHONE_DISPLAY)}
        >
          {LODGE_PHONE_DISPLAY}
        </a>.

      </Section>

      <Section title="Children">
        Our website is intended for adults booking lodging. We do not knowingly collect
        information from children under 13 without a parent's involvement.
      </Section>

      <Section title="Changes to this policy">
        We may update this policy from time to time. The "Last updated" date at the top
        always reflects the most recent version.
      </Section>

      <div className="mt-12 border-t border-[var(--electric)]/15 pt-6 text-sm text-[var(--offwhite-warm)]/80">
        Questions? <Link to="/contact" className="text-[var(--electric)] hover:underline">Contact us</Link> or
        read our <Link to="/terms" className="text-[var(--electric)] hover:underline">Terms of Service</Link>.
      </div>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-2xl text-[var(--offwhite)]">{title}</h2>
      <p className="mt-3 text-[15px] leading-relaxed text-[var(--offwhite-warm)]/85">{children}</p>
    </section>
  );
}
